import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}
// Force backend timestamps to be treated as UTC if no timezone is present
function parseBackendDate(value) {
  if (!value) return null;

  if (value instanceof Date) return value;

  if (typeof value === 'string') {
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(value);

    // If no timezone → treat as UTC
    if (!hasTimezone && value.includes('T')) {
      return new Date(value + 'Z');
    }

    return new Date(value);
  }

  return new Date(value);
}

// Format date for display (in Australia/Sydney timezone)
export function formatDate(date, options = {}) {
  if (!date) return '-';
  const d = parseBackendDate(date);
  if (!d || isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Australia/Sydney',
    ...options
  });
}

// Format datetime for display (in Australia/Sydney timezone)
export function formatDateTime(date) {
  if (!date) return '-';
  const d = parseBackendDate(date);
  if (!d || isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Australia/Sydney'
  });
}

// Format time only (in Australia/Sydney timezone)
export function formatTime(date) {
  if (!date) return '-';

  try {
    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
      return '-';
    }

    return d.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Australia/Sydney',
    });
  } catch (e) {
    console.error('formatTime error:', e);
    return '-';
  }
}

// Get relative time
export function getRelativeTime(date) {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(d);
}

// Severity colors
export const severityColors = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/50',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
};

// Status colors
export const statusColors = {
  draft: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50',
  submitted: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  under_review: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  released: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  closed: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50',
};

// Patrol status colors
export const patrolStatusColors = {
  not_started: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  incomplete: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  failed: 'bg-red-500/20 text-red-400 border-red-500/50',
};

// Shift status colors
export const shiftStatusColors = {
  scheduled: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  cancelled: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50',
  no_show: 'bg-red-500/20 text-red-400 border-red-500/50',
};

// Format status for display
export function formatStatus(status) {
  if (!status) return '-';
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Format role for display
export function formatRole(role) {
  if (!role) return '-';
  return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Get user initials
export function getInitials(firstName, lastName) {
  const first = firstName?.[0] || '';
  const last = lastName?.[0] || '';
  return (first + last).toUpperCase();
}
